import styled from 'styled-components';
import { BookmarkCheck } from 'lucide-react';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import PostCard from '../components/blog/PostCard';
import { Container } from '../components/ui/Container';
import SEO from '../components/seo/SEO';
import { useBookmark } from '../hooks/useBookmark';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 2rem;
    
    @media (min-width: 768px) {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }
    
    @media (min-width: 1024px) {
      padding-top: 4rem;
      padding-bottom: 4rem;
    }
  }
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 2rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 0.75rem;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 3.5rem;
  }
`;

const PageDescription = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.mutedForeground};
  max-width: 42rem;
  line-height: 1.7;
  
  @media (min-width: 768px) {
    font-size: 1.125rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.25rem;
  }
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 4rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.75rem;
  }
  
  @media (min-width: 768px) {
    gap: 2rem;
    margin-bottom: 5rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
    margin-bottom: 6rem;
  }
  
  @media (min-width: 1440px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
  }
  
  @media (min-width: 1920px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 3rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  
  @media (min-width: 768px) {
    padding: 6rem 2rem;
  }
`;

const EmptyIcon = styled.div`
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1.5rem;
  color: ${props => props.theme.colors.mutedForeground};
  opacity: 0.5;
  
  @media (min-width: 1024px) {
    width: 5rem;
    height: 5rem;
    margin-bottom: 2rem;
  }
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const EmptyTitle = styled.h2`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.5rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 0.75rem;
  
  @media (min-width: 1024px) {
    font-size: 2rem;
  }
`;

const EmptyText = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  max-width: 32rem;
  margin-left: auto;
  margin-right: auto;
  
  @media (min-width: 1024px) {
    font-size: 1.125rem;
  }
`;

const Bookmarks = () => {
  const { bookmarks, loading } = useBookmark();

  // Extract articles from bookmarks (handle both server and localStorage formats)
  const articles = bookmarks.map(bookmark => {
    // If bookmark has an article property (server format), use that
    if (bookmark.article) {
      return bookmark.article;
    }
    // Otherwise, use the bookmark itself (localStorage format)
    return bookmark;
  });

  return (
    <PageWrapper>
      <SEO
        title="My Bookmarks"
        description="View your saved articles on Bloomwik"
        url="/bookmarks"
        noindex
      />
      <Header />
      <Main>
        <Container>
          <PageHeader>
            <PageTitle>My Bookmarks</PageTitle>
            <PageDescription>
              Articles you've saved for later reading.
            </PageDescription>
          </PageHeader>

          {loading ? (
            <EmptyState>
              <EmptyText>Loading bookmarks...</EmptyText>
            </EmptyState>
          ) : articles.length > 0 ? (
            <PostsGrid>
              {articles.map((article, index) => (
                <PostCard
                  key={`${article.slug || article._id || article.id || article.title}-${index}`}
                  {...article}
                  index={index}
                />
              ))}
            </PostsGrid>
          ) : (
            <EmptyState>
              <EmptyIcon>
                <BookmarkCheck />
              </EmptyIcon>
              <EmptyTitle>No Bookmarks Yet</EmptyTitle>
              <EmptyText>
                Start saving articles you want to read later by clicking the bookmark button on any article.
              </EmptyText>
            </EmptyState>
          )}
        </Container>
      </Main>
      <Footer />
    </PageWrapper>
  );
};

export default Bookmarks;



